import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CloudArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { UPSTREAM_LOOKUP_NETWORKS } from "./constants";

/**
 * Amber upstream-lookup prompt that appears when the local search
 * returns zero rows. Lets admin pivot a hop straight into the upstream
 * partner's own catalog so they don't have to leave this screen to
 * look up a brand we haven't pulled yet.
 *
 * Only shows when:
 *   • search input is ≥ 2 chars (avoids flashing on single chars), and
 *   • the filtered table is empty, and
 *   • we're not currently loading.
 */
export default function EmptySearchPrompt({
  searchInput, networkFilter, brandsLength, loading, onLookupComplete,
}) {
  const q = searchInput.trim();
  if (q.length < 2 || brandsLength !== 0 || loading) return null;

  const networks = UPSTREAM_LOOKUP_NETWORKS.filter(
    (n) => networkFilter === "all" || networkFilter === n,
  );

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-md p-3 mb-4" data-testid="empty-search-upstream-prompt">
      <div className="text-sm font-medium text-amber-900 mb-2">
        No brands matched "{q}" in the catalog.
      </div>
      <div className="text-xs text-amber-800 mb-2">
        Search the upstream feed partner directly — matches will be auto-imported into your catalog:
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {networks.map((n) => (
          <Button
            key={n}
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={async () => {
              try {
                const { data } = await API.post(`/admin/networks/${n}/lookup-brand`, { q });
                if (data.found > 0) {
                  toast.success(`Found ${data.found} on ${n} — imported into catalog`);
                  onLookupComplete?.();
                } else {
                  toast.info(`No matches on ${n} for "${q}"`);
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || `${n} lookup failed`);
              }
            }}
            data-testid={`upstream-lookup-${n}`}
          >
            <CloudArrowDown size={12} className="mr-1" />
            Check {n}
          </Button>
        ))}
      </div>
    </div>
  );
}
